import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useDebounced } from '../lib/hooks.js';
import { ErrorNote, Spinner } from './ui.jsx';

/**
 * Type-ahead over the master items list, with an explicit "create it" escape
 * hatch. Search runs through the same matcher the receipt pipeline uses, so
 * typing receipt shorthand finds the right item here too.
 */
export default function ItemPicker({ value, onChange, placeholder = 'Search items…', allowCreate = true, autoFocus }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [selected, setSelected] = useState(value || null);
  const debounced = useDebounced(query, 220);

  // The review screens apply a suggestion chip by setting the parent's value
  // directly, without going through pick(). Reading the prop only at mount left
  // this box empty while the match silently took -- which looked like the tap
  // failed and invited creating the duplicate the chip existed to prevent.
  useEffect(() => { setSelected(value || null); }, [value]);

  useEffect(() => {
    let cancelled = false;
    // Also clears a stale spinner: emptying the box cancels the in-flight
    // search, whose finally() is then not allowed to run.
    if (!debounced.trim()) { setResults([]); setLoading(false); return undefined; }
    setLoading(true);
    api.get(`/items/search?q=${encodeURIComponent(debounced)}&limit=8`)
      .then((data) => {
        if (cancelled) return;
        const list = data.best
          ? [data.best, ...data.suggestions.filter((s) => s.id !== data.best.id)]
          : data.suggestions;
        setResults(list);
      })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debounced]);

  const pick = (item) => {
    setSelected(item);
    setQuery('');
    setResults([]);
    onChange(item);
  };

  const create = async () => {
    const name = query.trim();
    if (!name) return;
    // Caught and shown here, not left to escape the click handler: a rejected
    // create ("… normalizes to nothing usable", or no network) otherwise
    // changes nothing on screen and the button reads as dead.
    setCreating(true);
    setCreateError(null);
    try {
      pick(await api.post('/items', { name }));
    } catch (error) {
      setCreateError(error);
    } finally {
      setCreating(false);
    }
  };

  if (selected) {
    return (
      <div className="row">
        <div className="grow truncate">
          <strong>{selected.name}</strong>
          {selected.brand && <span className="muted small"> · {selected.brand}</span>}
        </div>
        <button
          type="button"
          className="small ghost"
          onClick={() => { setSelected(null); onChange(null); }}
        >
          change
        </button>
      </div>
    );
  }

  return (
    <div>
      <input
        type="search"
        value={query}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(event) => setQuery(event.target.value)}
        aria-label="Search master items"
      />
      {loading && <div className="small muted" style={{ marginTop: 6 }}><Spinner /> searching…</div>}
      {results.length > 0 && (
        <div className="card list" style={{ marginTop: 8, padding: '4px 12px' }}>
          {results.map((item) => (
            <button
              key={item.id}
              type="button"
              className="ghost"
              style={{ justifyContent: 'flex-start', width: '100%', textAlign: 'left' }}
              onClick={() => pick(item)}
            >
              <span className="grow truncate">
                {item.name}
                {item.brand && <span className="muted small"> · {item.brand}</span>}
              </span>
              {item.score != null && <span className="tiny muted">{Math.round(item.score * 100)}%</span>}
            </button>
          ))}
        </div>
      )}
      {allowCreate && debounced.trim() && !loading && (
        <button type="button" className="small full" style={{ marginTop: 8 }} disabled={creating} onClick={create}>
          {creating ? <><Spinner /> Creating…</> : <>+ Create “{query.trim()}”</>}
        </button>
      )}
      {createError && <div style={{ marginTop: 8 }}><ErrorNote error={createError} /></div>}
    </div>
  );
}
